import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Header,
  Modal,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationTrackStarsRunPattern,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationPopulationBuilder } from "./InvestigationPopulationBuilder";
import {
  InvestigationRule,
  caseRules,
  investigationPath,
  isTerminal,
  ownerLabel,
  ruleToSeed,
} from "./investigationUtils";
import { shortPatternId } from "./trackstarsText";

/** A pattern the categoriser can learn: it names a category and TrackStars wrote a clause for it. */
export const isCategorisable = (p: InvestigationTrackStarsRunPattern) =>
  !!p.category && !!p.whenClause?.trim();

/**
 * The categoriser change for the chosen patterns, ready to paste into the PySpark categoriser:
 * one commented `.when(...)` per pattern (TrackStars' clause verbatim, wrapped when it is a bare
 * condition), largest first, with where it came from so a reviewer can trace it back.
 */
export const categoriserSnippet = (
  investigation: Investigation,
  runId: string,
  patterns: InvestigationTrackStarsRunPattern[]
): string => {
  const lines = [
    `# TrackStars categorisation run ${runId} on ${investigation.id} (${investigation.title})`,
  ];
  patterns.filter(isCategorisable).forEach((p) => {
    const clause = (p.whenClause as string).trim();
    lines.push(
      `# ${p.patternId} ${p.category ?? ""} — ${p.items} items here${
        p.confidencePct ? `, ${p.confidence ?? ""} ${p.confidencePct}%` : ""
      }`,
      clause.startsWith(".when(")
        ? clause
        : `.when(${clause}, "${p.category as string}")`
    );
  });
  return `${lines.join("\n")}\n`;
};

const pct = (part: number, total: number) =>
  total ? `${Math.round((1000 * part) / total) / 10}%` : "0%";

/** The parent's first rule branch plus the pattern predicates it does not already pin. */
export const splitRuleFor = (
  parent: InvestigationRule,
  pattern: InvestigationRule
): InvestigationRule => {
  const pinned = new Set(parent.map((p) => p.field as string));
  return [...parent, ...pattern.filter((p) => !pinned.has(p.field as string))];
};

interface SplitPatternProps {
  stage: Stage;
  investigation: Investigation;
  runId: string;
  pattern: InvestigationTrackStarsRunPattern;
  disabledReason?: string;
}

/** "Split out": the split builder, seeded with the parent rule narrowed to this pattern. */
const SplitPatternButton: React.FC<SplitPatternProps> = ({
  stage,
  investigation,
  runId,
  pattern,
  disabledReason,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const parentRule = useMemo(
    () => caseRules(investigation)[0] ?? [],
    [investigation]
  );
  const seed = useMemo(
    () => ruleToSeed(splitRuleFor(parentRule, pattern.rule)),
    [parentRule, pattern.rule]
  );
  return (
    <>
      <Button
        variant="inline-link"
        disabled={!!disabledReason}
        disabledReason={disabledReason}
        ariaLabel={`Split out ${
          pattern.category ?? shortPatternId(pattern.patternId)
        }`}
        onClick={() => setOpen(true)}
      >
        Split out
      </Button>
      {open && (
        <Modal
          visible
          size="large"
          onDismiss={() => setOpen(false)}
          header={
            <Header
              variant="h2"
              description={`A child investigation for TrackStars pattern ${shortPatternId(
                pattern.patternId
              )}. It takes over the items that match; this investigation keeps the rest.`}
            >
              Split out {pattern.category ?? shortPatternId(pattern.patternId)}
            </Header>
          }
        >
          <InvestigationPopulationBuilder
            stage={stage}
            seed={seed}
            splitFrom={{
              investigationId: investigation.id,
              title: investigation.title,
              rule: investigation.rule || [],
              pattern: {
                patternId: pattern.patternId,
                runId,
                category: pattern.category,
                description: pattern.description,
                rule: pattern.rule,
              },
            }}
            onCreated={(childId) => {
              setOpen(false);
              navigate(investigationPath(childId, stage));
            }}
            onCancel={() => setOpen(false)}
          />
        </Modal>
      )}
    </>
  );
};

interface TrackStarsRunResultProps {
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  loading?: boolean;
  onRename?: (title: string) => void;
}

/**
 * The result of a completed categorisation run: how much of the population its patterns explain,
 * each pattern with its item count here, "Split out" per pattern, and the title the run proposes
 * when it did not rename the investigation itself.
 */
export const TrackStarsRunResult: React.FC<TrackStarsRunResultProps> = ({
  stage,
  investigation,
  currentUser,
  loading = false,
  onRename,
}) => {
  const run = investigation.trackstarsRun;
  const result = run?.status === "COMPLETED" ? run.result : undefined;
  const [selected, setSelected] = useState<
    InvestigationTrackStarsRunPattern[] | null
  >(null);
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  const defaultSelection = useMemo(
    () =>
      (result?.patterns ?? []).filter((p) => isCategorisable(p) && p.items > 0),
    [result?.patterns]
  );
  if (!run || !result) return null;
  const chosen = selected ?? defaultSelection;
  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(
        categoriserSnippet(investigation, run.runId, chosen)
      );
      setCopied("copied");
    } catch (err) {
      console.error(err);
      setCopied("failed");
    }
  };
  const isOwner = !!currentUser && investigation.owner === currentUser;
  const splitBlocked = isTerminal(investigation.status)
    ? "This investigation is closed."
    : !isOwner
    ? `Only ${ownerLabel(investigation.owner)} can split this investigation.`
    : undefined;
  const unexplained = result.totalItems - result.explainedItems;
  const offerTitle =
    !!result.suggestedTitle &&
    !result.renamed &&
    result.suggestedTitle !== investigation.title;

  return (
    <Container
      data-testid="trackstars-run-result"
      header={
        <Header
          variant="h3"
          actions={
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {copied === "copied" && (
                <StatusIndicator type="success">Copied</StatusIndicator>
              )}
              {copied === "failed" && (
                <StatusIndicator type="error">Copy failed</StatusIndicator>
              )}
              <Button
                iconName="copy"
                disabled={!chosen.some(isCategorisable)}
                onClick={() => {
                  copySnippet().catch(() => undefined);
                }}
              >
                Copy categoriser change ({chosen.filter(isCategorisable).length}
                )
              </Button>
            </SpaceBetween>
          }
          description={`Run ${run.runId}${
            result.reportId && result.reportId !== run.runId
              ? ` (report ${result.reportId})`
              : ""
          }`}
        >
          Categorisation
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ProgressBar
          value={result.explainedPct}
          label="Explained by TrackStars patterns"
          additionalInfo={`${result.explainedItems.toLocaleString()} of ${result.totalItems.toLocaleString()} items${
            unexplained > 0
              ? ` · ${unexplained.toLocaleString()} unexplained`
              : ""
          }`}
        />
        {offerTitle && (
          <Box data-testid="trackstars-suggested-title">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <span>
                Suggested title: <strong>{result.suggestedTitle}</strong>
              </span>
              {onRename && (
                <Button
                  disabled={loading}
                  onClick={() => onRename(result.suggestedTitle as string)}
                >
                  Use this title
                </Button>
              )}
            </SpaceBetween>
          </Box>
        )}
        {/* eslint-disable react/no-unstable-nested-components -- Table cell renderers are plain functions */}
        <Table
          variant="embedded"
          wrapLines={false}
          items={result.patterns}
          trackBy="patternId"
          selectionType="multi"
          selectedItems={chosen}
          isItemDisabled={(p) => !isCategorisable(p)}
          onSelectionChange={({ detail }) => {
            setCopied("idle");
            setSelected(detail.selectedItems);
          }}
          ariaLabels={{
            selectionGroupLabel: "Patterns for the categoriser change",
            allItemsSelectionLabel: () => "Select all patterns",
            itemSelectionLabel: (_, p) =>
              `Include ${
                p.category ?? shortPatternId(p.patternId)
              } in the categoriser change`,
          }}
          empty={<Box>TrackStars found no patterns.</Box>}
          columnDefinitions={[
            {
              id: "category",
              header: "Pattern",
              cell: (p) => (
                <span title={p.description || undefined}>
                  {p.category ?? shortPatternId(p.patternId)}
                </span>
              ),
            },
            {
              id: "items",
              header: "Items",
              cell: (p) =>
                `${p.items.toLocaleString()} (${pct(
                  p.items,
                  result.totalItems
                )})`,
            },
            {
              id: "confidence",
              header: "Confidence",
              cell: (p) =>
                p.confidencePct
                  ? `${p.confidence ?? ""} ${p.confidencePct}%`.trim()
                  : p.confidence ?? "—",
            },
            {
              id: "cause",
              header: "Root cause",
              cell: (p) => (
                <span title={p.rootCause || undefined}>
                  {p.rootCause || "—"}
                </span>
              ),
            },
            {
              id: "actions",
              header: "",
              cell: (p) =>
                p.items > 0 ? (
                  <SplitPatternButton
                    stage={stage}
                    investigation={investigation}
                    runId={run.runId}
                    pattern={p}
                    disabledReason={splitBlocked}
                  />
                ) : null,
            },
          ]}
        />
        {/* eslint-enable react/no-unstable-nested-components */}
      </SpaceBetween>
    </Container>
  );
};

export default TrackStarsRunResult;
